import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from './client';
import type { SupportedLocale } from './localized-string';
import type { QuestionSpec } from './workflows';

export type QuestionSetStatus = 'draft' | 'active' | 'archived';

export type LocaleMap = Partial<Record<SupportedLocale, string>>;

export interface QuestionSet {
  id: string;
  /** Null = product-wide default that applies to every org. */
  orgId: string | null;
  productId: string;
  name: string;
  description: string | null;
  /** Sidecar translations for the top-level `name` / `description` columns. */
  nameTranslations: LocaleMap | null;
  descriptionTranslations: LocaleMap | null;
  version: number;
  status: QuestionSetStatus;
  questions: QuestionSpec[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

export interface QuestionSetList {
  items: QuestionSet[];
}

export interface ListQuestionSetsFilters {
  orgId?: string;
  productId?: string;
  status?: QuestionSetStatus;
}

const KEYS = {
  list: (filters: ListQuestionSetsFilters) => ['question-sets', 'list', filters] as const,
  detail: (id: string) => ['question-sets', 'detail', id] as const,
};

function buildListUrl(filters: ListQuestionSetsFilters): string {
  const params = new URLSearchParams();
  if (filters.orgId) params.set('org_id', filters.orgId);
  if (filters.productId) params.set('product_id', filters.productId);
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();
  return qs ? `/api/question-sets?${qs}` : '/api/question-sets';
}

export function useQuestionSets(filters: ListQuestionSetsFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => apiGet<QuestionSetList>(buildListUrl(filters)),
  });
}

export function useQuestionSet(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.detail(id) : ['question-sets', 'detail', 'idle'],
    queryFn: () => apiGet<QuestionSet>(`/api/question-sets/${id}`),
    enabled: !!id,
  });
}

// snake_case at the network boundary mirrors the admin-api controller's
// body shape (which mirrors the orchestrator's Zod schema).
export interface CreateQuestionSetInput {
  /** Required for admin operators (no org on JWT). Ignored when `is_default: true`. */
  org_id?: string;
  /**
   * Product-wide default — applies to every org with this product
   * until they author their own override. Only cross-tenant admins
   * may set this; tenant clients sending `true` are refused.
   */
  is_default?: boolean;
  product_id: string;
  name: string;
  description?: string;
  /** Sidecar translations for the top-level `name` / `description`. */
  nameTranslations?: LocaleMap;
  descriptionTranslations?: LocaleMap;
  questions: QuestionSpec[];
}

export function useCreateQuestionSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQuestionSetInput) =>
      apiPost<QuestionSet>('/api/question-sets', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['question-sets', 'list'] });
    },
  });
}

export interface UpdateQuestionSetInput {
  name?: string;
  description?: string;
  nameTranslations?: LocaleMap;
  descriptionTranslations?: LocaleMap;
  questions?: QuestionSpec[];
}

export function useUpdateQuestionSet(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateQuestionSetInput) =>
      apiPatch<QuestionSet>(`/api/question-sets/${id}`, input),
    onSuccess: (saved) => {
      qc.setQueryData(['question-sets', 'detail', id], saved);
      qc.invalidateQueries({ queryKey: ['question-sets', 'list'] });
    },
  });
}

export function usePublishQuestionSet(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<QuestionSet>(`/api/question-sets/${id}/publish`),
    onSuccess: (saved) => {
      qc.setQueryData(['question-sets', 'detail', id], saved);
      qc.invalidateQueries({ queryKey: ['question-sets', 'list'] });
    },
  });
}

export function useArchiveQuestionSet(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<QuestionSet>(`/api/question-sets/${id}/archive`),
    onSuccess: (saved) => {
      qc.setQueryData(['question-sets', 'detail', id], saved);
      qc.invalidateQueries({ queryKey: ['question-sets', 'list'] });
    },
  });
}

export interface CloneQuestionSetInput {
  org_id?: string;
  product_id: string;
  name: string;
}

export function useCloneQuestionSet(sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CloneQuestionSetInput) =>
      apiPost<QuestionSet>(`/api/question-sets/${sourceId}/clone`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['question-sets', 'list'] });
    },
  });
}

export function useNewVersionQuestionSet(sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<QuestionSet>(`/api/question-sets/${sourceId}/new-version`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['question-sets', 'list'] });
    },
  });
}
